import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { discountMembersApi } from '../services/api';

const emptyForm = { email: '', startCode: '' };

const fieldLabelStyle = {
  marginBottom: '0.75rem',
  fontWeight: '600',
  color: '#495057',
  fontSize: '0.95rem',
};

/** Rounded pill badge, same shape/padding as the Users and API Keys pages. */
const pillStyle = (bg, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0.375rem 0.875rem',
  backgroundColor: bg,
  color,
  borderRadius: '16px',
  fontSize: '0.8125rem',
  fontWeight: '600',
  letterSpacing: '0.025em',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  whiteSpace: 'nowrap',
});

const STATUS_COLORS = {
  active: { bg: '#d4edda', color: '#155724' },
  removed: { bg: '#f8d7da', color: '#721c24' },
};

const memberStatus = (row) => (row.active ? 'active' : 'removed');

const STATUS_LABELS = {
  active: 'Active',
  removed: 'Deactivated',
};

const DiscountMembers = () => {
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dialogVisible, setDialogVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const fetchMembers = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      const data = await discountMembersApi.list({ search: searchTerm || undefined, limit: 200 });
      setMembers(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load discount members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMembers(search);
  };

  const handleAdd = () => {
    setFormData(emptyForm);
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.startCode.trim()) {
      setError('Start code is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await discountMembersApi.create({
        email: formData.email.trim(),
        startCode: formData.startCode.trim(),
      });
      setDialogVisible(false);
      setSuccess(`Added ${formData.email} — active immediately.`);
      setTimeout(() => setSuccess(''), 4000);
      fetchMembers(search);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to add discount member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = (row) => {
    const makeActive = !row.active;
    confirmDialog({
      message: makeActive
        ? `Re-activate ${row.email} (${row.startCode})? They will be able to receive Discount Price again once verified active.`
        : `Deactivate ${row.email} (${row.startCode})? They will stop receiving Discount Price immediately through the Public API.`,
      header: makeActive ? 'Re-activate Member' : 'Deactivate Member',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: makeActive ? undefined : 'p-button-danger',
      accept: async () => {
        try {
          await discountMembersApi.setActive(row.startCode, makeActive);
          setSuccess(makeActive ? 'Member re-activated' : 'Member deactivated');
          setTimeout(() => setSuccess(''), 3000);
          fetchMembers(search);
        } catch (err) {
          setError(err.response?.data?.message || err.message || 'Failed to update member');
        }
      },
    });
  };

  const handleDelete = (row) => {
    confirmDialog({
      message: `Permanently remove ${row.email} (${row.startCode}) from the Discount Members list? This cannot be undone — to just pause access, use Deactivate instead.`,
      header: 'Remove Discount Member',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await discountMembersApi.remove(row.startCode);
          setSuccess('Member removed');
          setTimeout(() => setSuccess(''), 3000);
          fetchMembers(search);
        } catch (err) {
          setError(err.response?.data?.message || err.message || 'Failed to remove member');
        }
      },
    });
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

  const statusBodyTemplate = (row) => {
    const status = memberStatus(row);
    const c = STATUS_COLORS[status];
    return <span style={pillStyle(c.bg, c.color)}>{STATUS_LABELS[status]}</span>;
  };

  const startCodeBodyTemplate = (row) => <span style={{ fontFamily: 'monospace' }}>{row.startCode}</span>;
  const addedOnBodyTemplate = (row) => formatDate(row.createdAt);
  const addedByBodyTemplate = (row) => row.createdBy || '—';

  const actionsBodyTemplate = (row) => (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
      <Button
        icon={row.active ? 'pi pi-ban' : 'pi pi-check'}
        className={`p-button-rounded p-button-text p-button-sm ${row.active ? 'p-button-warning' : 'p-button-success'}`}
        onClick={() => handleToggleActive(row)}
        tooltip={row.active ? 'Deactivate' : 'Re-activate'}
        tooltipOptions={{ position: 'top' }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-text p-button-danger p-button-sm"
        onClick={() => handleDelete(row)}
        tooltip="Remove"
        tooltipOptions={{ position: 'top' }}
      />
    </div>
  );

  return (
    <div
      style={{
        padding: '2rem 2.5rem',
        maxWidth: '1400px',
        margin: '0 auto',
        minHeight: 'calc(100vh - 100px)',
      }}
    >
      <div
        style={{
          marginBottom: '2.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div>
          <h1 style={{ marginBottom: '0.5rem', fontSize: '2rem', fontWeight: '600', color: '#2c3e50' }}>
            Discount Members
          </h1>
          <p style={{ marginBottom: 0, color: '#6c757d', fontSize: '0.95rem' }}>
            The approved list of (email, start code) pairs allowed to receive Discount Price through the
            Public API. Only entries added here, by our own staff, can unlock the discount for a partner
            request.
          </p>
        </div>
        <Button
          label="Add Member"
          icon="pi pi-plus"
          iconPos="left"
          onClick={handleAdd}
          className="p-button-lg"
          style={{ padding: '0.75rem 2rem', fontSize: '1rem', fontWeight: '500' }}
        />
      </div>

      {error && (
        <Message severity="error" text={error} style={{ marginBottom: '1.5rem' }} onClose={() => setError('')} />
      )}
      {success && (
        <Message
          severity="success"
          text={success}
          style={{ marginBottom: '1.5rem' }}
          onClose={() => setSuccess('')}
        />
      )}

      <form onSubmit={handleSearch} style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.75rem' }}>
        <InputText
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email or start code"
          style={{ flexGrow: 1, maxWidth: '360px', padding: '0.6rem 0.75rem' }}
        />
        <Button label="Search" icon="pi pi-search" type="submit" className="p-button-outlined" />
      </form>

      <Card style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '1.5rem' }}>
        <DataTable
          value={members}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 20, 50]}
          emptyMessage="No discount members added yet"
          style={{ width: '100%' }}
          className="p-datatable-sm"
        >
          <Column field="email" header="Email" sortable style={{ minWidth: '220px' }} />
          <Column header="Start Code" body={startCodeBodyTemplate} style={{ minWidth: '150px' }} />
          <Column header="Status" body={statusBodyTemplate} style={{ minWidth: '170px' }} />
          <Column header="Added on" body={addedOnBodyTemplate} style={{ minWidth: '180px' }} />
          <Column header="Added by" body={addedByBodyTemplate} style={{ minWidth: '160px' }} />
          <Column header="Actions" body={actionsBodyTemplate} style={{ minWidth: '140px' }} exportable={false} />
        </DataTable>
        {total > members.length && (
          <p style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '0.75rem' }}>
            Showing {members.length} of {total} — refine your search to narrow results.
          </p>
        )}
      </Card>

      <Dialog
        header={
          <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0.5rem' }}>
            <i className="pi pi-user-plus" style={{ marginRight: '0.75rem', fontSize: '1.25rem' }}></i>
            <span style={{ fontSize: '1.25rem', fontWeight: '600' }}>Add a Discount Member</span>
          </div>
        }
        visible={dialogVisible}
        style={{ width: '90%', maxWidth: '500px' }}
        contentStyle={{ padding: '2rem 2.5rem' }}
        onHide={() => setDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1.5rem 2.5rem' }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              iconPos="left"
              onClick={() => setDialogVisible(false)}
              className="p-button-text p-button-lg"
              style={{ padding: '0.75rem 1.5rem', fontSize: '1rem' }}
              disabled={submitting}
            />
            <Button
              label="Add Member"
              icon={submitting ? null : 'pi pi-check'}
              iconPos="left"
              onClick={handleSubmit}
              disabled={submitting}
              loading={submitting}
              className="p-button-lg"
              style={{ padding: '0.75rem 1.5rem', fontSize: '1rem', minWidth: '160px' }}
            />
          </div>
        }
      >
        <div style={{ background: '#e7f3ff', border: '1px solid #b3d9ff', borderRadius: '8px', padding: '1rem', marginBottom: '1.75rem' }}>
          <p style={{ margin: 0, color: '#0056b3', fontSize: '0.9rem' }}>
            <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
            Only add a start code you've confirmed belongs to this email — this list is the only thing
            standing between a partner request and our negotiated price. It becomes active the moment you
            submit, with no further check.
          </p>
        </div>

        <div className="p-field" style={{ marginBottom: '1.75rem' }}>
          <label htmlFor="email" className="p-d-block" style={fieldLabelStyle}>
            Email *
          </label>
          <InputText
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
            placeholder="member@example.com"
          />
        </div>

        <div className="p-field" style={{ marginBottom: '0.5rem' }}>
          <label htmlFor="startCode" className="p-d-block" style={fieldLabelStyle}>
            QuikQ Start Code *
          </label>
          <InputText
            id="startCode"
            value={formData.startCode}
            onChange={(e) => setFormData({ ...formData, startCode: e.target.value })}
            style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', fontFamily: 'monospace' }}
            placeholder="e.g. 994056"
          />
        </div>

        {error && <Message severity="error" text={error} style={{ marginTop: '1rem', width: '100%' }} />}
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default DiscountMembers;
